import type { TFunction } from "i18next"
import type { WorkflowJob } from "@/reducers/openclawWorkflowsSlice"
import { stopActionLabel } from "@/reducers/routes2gSlice"
import i18n from "@/lib/i18n/i18n"

export type StopPhase = "arrival" | "departure"

/** The only two `on` values the agent accepts for a stop rule. */
export const ARRIVED_EVENT = "route.stop_arrived"
export const LEFT_EVENT = "route.stop_left"

/**
 * Documents only exist once unloading is done, so a delivery stop defaults to
 * the departure moment; everything else is about being there.
 */
export function defaultPhaseFor(stopAction?: string): StopPhase {
  return stopAction === "delivery" ? "departure" : "arrival"
}

/** `undefined` for every schedule that is not a stop event. */
export function phaseOf(job: WorkflowJob): StopPhase | undefined {
  const schedule = "schedule" in job ? job.schedule : null
  if (schedule?.kind !== "event") return undefined
  if (schedule.on === LEFT_EVENT) return "departure"
  if (schedule.on === ARRIVED_EVENT) return "arrival"
  return undefined
}

const scheduleOf = (job: WorkflowJob) =>
  "schedule" in job ? job.schedule : null

/**
 * Both sets genuinely fire at this stop, but only the pinned ones may be
 * edited here — changing a fleet-wide rule from one stop's menu would silently
 * change every other stop too.
 */
export function rulesForStop(
  jobs: WorkflowJob[],
  stopId: string,
): { pinned: WorkflowJob[]; broader: WorkflowJob[] } {
  const stopEvents = jobs.filter((j) => phaseOf(j) !== undefined)
  return {
    pinned: stopEvents.filter((j) => scheduleOf(j)?.stopId === stopId),
    broader: stopEvents.filter((j) => !scheduleOf(j)?.stopId),
  }
}

/**
 * The frontend parser drops a workflow with no name, so a rule is never
 * created or renamed without one.
 */
export function nameFor(stopLabel: string, phase: StopPhase): string {
  return `${stopLabel} — ${phase === "departure" ? "on departure" : "on arrival"}`
}

/** The stored instruction, so an editor can prefill what is already set. */
export function messageOf(job: WorkflowJob): string {
  return "message" in job ? (job.message ?? "") : ""
}

/** Scope line for a broader rule, e.g. "every delivery · truck1@…". */
export function describeBroaderScope(
  job: WorkflowJob,
  t: TFunction = i18n.t,
): string {
  const schedule = scheduleOf(job)
  const action = schedule?.stopAction
  const what =
    action === "pickup"
      ? t("maps.stopAutomationScope.everyPickup")
      : action === "delivery"
        ? t("maps.stopAutomationScope.everyDelivery")
        : action
          ? t("maps.stopAutomationScope.everyAction", {
              action,
              label: stopActionLabel(t, action) ?? action,
            })
          : t("maps.stopAutomationScope.everyStop")
  return schedule?.targetJid ? `${what} · ${schedule.targetJid}` : what
}
